import { CircleAlert, Lock } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/api/errors'
import { Button } from './button'

interface EmptyStateProps {
  icon?: ReactNode
  eyebrow?: string
  title: string
  description?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ icon, eyebrow, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-start gap-3 px-6 py-10 sm:px-10', className)}>
      {icon && (
        <div className="grid size-10 place-items-center rounded-full border border-border bg-surface-sunken text-ink-muted [&_svg]:size-[18px]">
          {icon}
        </div>
      )}
      <div className="grid gap-1.5">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h3 className="display text-2xl text-ink">{title}</h3>
        {description && <div className="text-sm leading-relaxed text-ink-muted">{description}</div>}
      </div>
      {action}
    </div>
  )
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  return (
    <div role="alert" className={cn('flex flex-col items-start gap-3 px-6 py-8', className)}>
      <div className="flex items-center gap-2 text-sm font-semibold text-danger">
        <CircleAlert className="size-4" aria-hidden="true" />
        Couldn&apos;t load this
      </div>
      <p className="text-sm text-ink-muted">{errorMessage(error)}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

export function UnauthorizedState({ message }: { message?: string }) {
  return (
    <EmptyState
      icon={<Lock />}
      eyebrow="Restricted"
      title="You don't have access to this"
      description={message ?? 'Ask a workspace owner or admin if you need this permission.'}
    />
  )
}
